import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import type { FilterValue } from "@elastic/search-ui";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Box, styled } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";

const OLDEST = "2000-01-01T00:00:00.000Z";
const NEWEST = "2099-01-01T00:00:00.000Z";

interface Filter {
  field?: string;
  values?: { from: string; to: string }[];
}

export interface DateRangeFilterProps {
  addFilter: (filterName: string, filter: FilterValue) => void;
  removeFilter: (filterName: string) => void;
  filters?: Filter[];
  resetSignal: number;
}

const DatePickerContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
}));

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  addFilter,
  removeFilter,
  filters,
  resetSignal,
}: DateRangeFilterProps) => {
  const filterValue = filters?.find((filter) => filter.field === "created")?.values?.[0];
  const [dateRange, setDateRange] = useState<{ from: Dayjs | null; to: Dayjs | null }>({
    from: filterValue?.from && filterValue.from !== OLDEST ? dayjs(filterValue?.from) : null,
    to: filterValue?.to && filterValue.to !== NEWEST ? dayjs(filterValue?.to) : null,
  });

  useEffect(() => {
    if (resetSignal > 0) {
      setDateRange({ from: null, to: null });
    }
  }, [resetSignal]);

  useEffect(() => {
    removeFilter("created");
    if (dateRange.from || dateRange.to) {
      addFilter("created", {
        from: dateRange.from?.toISOString() ?? OLDEST,
        to: dateRange.to?.toISOString() ?? NEWEST,
        name: "created",
      });
    }
  }, [dateRange]);

  const validateDateString = (date: Dayjs | null): boolean => {
    return date?.toString() !== "Invalid Date";
  };

  const handleStartDateChange = (newValue: Dayjs | null) => {
    if (!validateDateString(newValue)) return;
    setDateRange({ from: newValue, to: dateRange.to });
  };

  const handleEndDateChange = (newValue: Dayjs | null) => {
    if (!validateDateString(newValue)) return;
    setDateRange({ from: dateRange.from, to: newValue });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-nz">
      <DatePickerContainer>
        <DatePicker
          label="Start Date"
          format="DD/MM/YYYY"
          value={dateRange.from}
          onChange={(newValue) => {
            handleStartDateChange(newValue);
          }}
          slotProps={{ field: { clearable: true } }}
        />
      </DatePickerContainer>
      <DatePickerContainer>
        <DatePicker
          label="End Date"
          format="DD/MM/YYYY"
          value={dateRange.to}
          onChange={(newValue) => {
            handleEndDateChange(newValue);
          }}
          slotProps={{ field: { clearable: true } }}
        />
      </DatePickerContainer>
    </LocalizationProvider>
  );
};
